import { z } from 'zod'
import { formatAsBrNumber, formatAsMoney } from '~/prix/utils/types/number'
import type { RuralReportTemplateProps } from '../../report/ruralReportTemplateTitle'

export const semenStockReportTypeSchema = z.enum([
  'monthlyStockByBull',
  'monthlyAccounting',
  'currentStockByBull',
])

export type SemenStockReportType = z.infer<typeof semenStockReportTypeSchema>

export interface SemenStockMonthlyByBullRow {
  month?: Date | string | null
  bullId: string
  bullName: string
  bullBreed?: string | null
  previousBalanceQuantity: number
  previousBalanceValue: number
  entryQuantity: number
  entryValue: number
  deliveryQuantity: number
  deliveryValue: number
  currentBalanceQuantity: number
  currentBalanceValue: number
}

export interface SemenStockMonthlyAccountingRow {
  month?: Date | string | null
  entryQuantity: number
  entryValue: number
  deliveryQuantity: number
  deliveryValue: number
  currentStockQuantity: number
  currentStockValue: number
}

export interface RuralSemenStockReportProps extends RuralReportTemplateProps {
  reportType: SemenStockReportType
  referenceMonth?: Date | string | null
  data?: SemenStockMonthlyByBullRow[] | SemenStockMonthlyAccountingRow[]
}

export const semenStockReportTitleMap: Record<SemenStockReportType, string> = {
  monthlyStockByBull: 'Estoque mensal de sêmen por touro',
  monthlyAccounting: 'Estoque de sêmen',
  currentStockByBull: 'Estoque atual',
}

export const semenStockReportColor = '#00A859'

export function buildSemenStockReportBaseName(reportType: SemenStockReportType) {
  switch (reportType) {
    case 'monthlyAccounting':
      return 'semen-stock-monthly-accounting-report'
    case 'currentStockByBull':
      return 'semen-stock-current-by-bull-report'
    default:
      return 'semen-stock-monthly-by-bull-report'
  }
}

function extractMonthAndYear(value?: Date | string | null) {
  if (!value) {
    return null
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null
    }

    return {
      month: value.getUTCMonth() + 1,
      year: value.getUTCFullYear(),
    }
  }

  const isoMonthMatch = value.match(/^(\d{4})-(\d{2})/)

  if (isoMonthMatch) {
    return {
      year: Number(isoMonthMatch[1]),
      month: Number(isoMonthMatch[2]),
    }
  }

  const parsedDate = new Date(value)

  if (Number.isNaN(parsedDate.getTime())) {
    return null
  }

  return {
    month: parsedDate.getUTCMonth() + 1,
    year: parsedDate.getUTCFullYear(),
  }
}

export function formatSemenStockReportMonth(value?: Date | string | null) {
  const monthAndYear = extractMonthAndYear(value)

  if (!monthAndYear) {
    if (!value) {
      return 'Não informado'
    }

    return String(value)
  }

  return `${String(monthAndYear.month).padStart(2, '0')}/${monthAndYear.year}`
}

export function buildSemenStockReportTitle(
  reportType: SemenStockReportType,
  referenceMonth?: Date | string | null,
) {
  const reportTitle = semenStockReportTitleMap[reportType]

  if (!referenceMonth) {
    return reportTitle
  }

  const formattedReferenceMonth = formatSemenStockReportMonth(referenceMonth)

  if (formattedReferenceMonth === 'Não informado') {
    return reportTitle
  }

  return `${reportTitle} - ${formattedReferenceMonth}`
}

export function formatSemenStockReportQuantity(value?: number | null) {
  return formatAsBrNumber(value ?? 0, 0)
}

export function formatSemenStockReportValue(value?: number | null) {
  return formatAsMoney(value ?? 0)
}
